import {
  SEASON_PALETTES,
  SEASONAL_VAR_KEYS,
  SeasonalThemeContext,
  SEASONS,
  type Season,
} from "@/hooks/use-seasonal-theme";
import { readInitialSeason, resolveSeason, browserThemeColor } from "@/lib/initial-season";
import { api } from "@/convex/_generated/api";
import { useQuery } from "convex/react";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useState } from "react";

export function SeasonalThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { resolvedTheme } = useTheme();
  const [initialSeason] = useState(readInitialSeason);
  // The active season is chosen by an admin from the back office and stored in
  // the database. Every visitor sees the same theme; there is no local override.
  const config = useQuery(api.crm.siteTheme.getPublic, {});

  const season = useMemo<Season>(() => {
    const raw = config?.season;
    if (raw && (SEASONS as readonly string[]).includes(raw)) {
      return raw as Season;
    }
    return config ? resolveSeason(config.season) : initialSeason;
  }, [config, initialSeason]);

  // Apply (or clear) the seasonal CSS variables on the document root whenever
  // the season or the resolved light/dark mode changes. We write inline vars on
  // <html>, which take precedence over the :root / .dark defaults in index.css.
  useEffect(() => {
    const root = document.documentElement;
    const mode = resolvedTheme === "dark" ? "dark" : "light";
    root.dataset.siteSeason = season;
    for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) { meta.removeAttribute("media"); meta.content = browserThemeColor(season, mode); }

    if (season === "none") {
      for (const key of SEASONAL_VAR_KEYS) root.style.removeProperty(key);
      return;
    }

    const vars = SEASON_PALETTES[season][mode];
    for (const key of SEASONAL_VAR_KEYS) {
      root.style.setProperty(key, vars[key]);
    }

    return () => {
      // Clear on unmount so we never leave stale overrides behind.
      for (const key of SEASONAL_VAR_KEYS) root.style.removeProperty(key);
      delete root.dataset.siteSeason;
    };
  }, [season, resolvedTheme]);

  const value = useMemo(() => ({ season }), [season]);

  return (
    <SeasonalThemeContext.Provider value={value}>
      {children}
    </SeasonalThemeContext.Provider>
  );
}
